import { DamageCodeFilter, DamageCodeFilterFormValue } from '../models/damage-code-filter.model';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, shareReplay, of, map, throwError } from 'rxjs';
import { DownloadRequest } from '../models/download-filter.model';
import { utils } from '../helper/utils';
import { DamageCodeHeader } from '../models/damage-code-header.model';
import { CodeDescription } from '../models/code-description.model';
import { DamageCodeResponse } from '../models/damage-code-response.model';
import { CreateDamageCodeRequest } from '../models/create-damage-code-request.model';
import { AuditLog } from '../models/audit-log.model';

type UpdateDamageCodeRequest = {
  description?: string;
  newDamageType?: number;
};

@Injectable({
  providedIn: 'root',
})
export class DamageCodeService {
  filterData: DamageCodeFilter = {};
  constructor(private readonly httpClient: HttpClient) {}

  createDamageCode(request: CreateDamageCodeRequest): Observable<DamageCodeResponse> {
    const url = utils.GetMasterDataManagementUrl('damage-code');
    return this.httpClient.post<DamageCodeResponse>(url, request);
  }

  updateDamageCode(damageCode: string, damageType: number, request: UpdateDamageCodeRequest): Observable<DamageCodeResponse> {
    const path = `damage-code/${encodeURIComponent(damageCode)}/${encodeURIComponent(damageType)}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.put<DamageCodeResponse>(url, request);
  }

  getDamageCode(page: number, pageLimit: number, sort?: string, filters?: DamageCodeFilter): Observable<HttpResponse<DamageCodeHeader[]>> {
    const params: { [param: string]: string | number | boolean | string[] } = {};
    params['page'] = page;
    params['limit'] = pageLimit > 0 ? pageLimit : 100;
    if (sort) {
      params['sort'] = sort;
    }
    if (filters) {
      if (
        filters.damageCodeDescriptionList &&
        Array.isArray(filters.damageCodeDescriptionList) &&
        filters.damageCodeDescriptionList.length > 0
      ) {
        params['damageCodeDescriptionList'] = filters.damageCodeDescriptionList.join(',');
      }
      if (filters.damageTypesList && Array.isArray(filters.damageTypesList) && filters.damageTypesList.length > 0) {
        params['damageTypesList'] = filters.damageTypesList.join(',');
      }
      if (filters?.searchText) {
        params['searchText'] = filters.searchText;
      }
    }

    const url = utils.GetMasterDataManagementUrl('damage-code');
    return this.httpClient.get<DamageCodeHeader[]>(url, { params, observe: 'response' }).pipe(
      map((response: HttpResponse<DamageCodeHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
          }));
          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
      catchError(() => {
        return of(new HttpResponse<DamageCodeHeader[]>({ body: [], headers: undefined }));
      }),
    );
  }

  /**
   * Fetches unique damage code key/value pairs and maps them to CodeDescription
   * Expected backend shape: [{ key: string, value: string }, ...]
   */
  getUniqueDamageCodes(): Observable<CodeDescription[]> {
    const url = utils.GetMasterDataManagementUrl('damage-code-all');
    return this.httpClient.get<{ key: string; value: string }[]>(url).pipe(
      map((list) => (list || []).map((item) => ({ code: item.key, description: item.value }))),
      catchError(() => of([])),
      shareReplay(1),
    );
  }

  /**
   * Fetch a single damage code by its key and optional damage type.
   * Endpoint: /api/damage-code/{damageCode}/{damageType}
   * Returns null on error to match other service patterns.
   */
  getDamageCodeByCode(damageCode: string, damageType?: string): Observable<DamageCodeResponse | null> {
    const path = damageType ? `damage-code/${damageCode}/${damageType}` : `damage-code/${damageCode}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.get<DamageCodeResponse>(url).pipe(catchError(() => of(null)));
  }

  /**
   * Soft-delete a damage code by code and damageType
   */
  deleteDamageCode(damageCode: string, damageType: number): Observable<void> {
    const path = `damage-code/${encodeURIComponent(damageCode)}/${encodeURIComponent(damageType)}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.delete<void>(url).pipe(catchError((err) => throwError(() => err)));
  }

  extractFilterOptions(data: DamageCodeHeader[]): {
    damageCodeDescriptionList: string[];
    damageTypesList: (string | number)[];
  } {
    return {
      damageCodeDescriptionList: Array.from(new Set(data.map((item) => item.damageCode).filter(Boolean))),
      damageTypesList: Array.from(new Set(data.map((item) => item.damageType).filter(Boolean))),
    };
  }

  private damageCodeFilterFormValue: DamageCodeFilterFormValue | null = null;
  setDamageCodeFilterFormValue(value: DamageCodeFilterFormValue | null) {
    this.damageCodeFilterFormValue = value;
  }
  getDamageCodeFilterFormValue(): DamageCodeFilterFormValue | null {
    return this.damageCodeFilterFormValue;
  }

  downloadToExcel(request: DownloadRequest): Observable<Blob> {
    const url = utils.GetDownloadUrl('damage');
    return this.httpClient.post(url, request, { responseType: 'blob' });
  }

  /**
   * GET audit logs for a damage code.
   * Endpoint: /api/damage-code/{damageCode}/{damageType}/audit-logs
   */
  getDamageCodeAuditLogs(
    damageCode: string,
    damageType: number,
    isSystemLog: boolean = false,
    page: number = 1,
    limit: number = 100,
  ): Observable<HttpResponse<AuditLog[]>> {
    const path = `damage-code/${encodeURIComponent(damageCode)}/${encodeURIComponent(damageType)}/audit-logs?isSystemLog=${isSystemLog}&page=${page}&limit=${limit}`;
    return this.httpClient
      .get<AuditLog[]>(utils.GetMasterDataManagementUrl(path), { observe: 'response' })
      .pipe(catchError(() => of(new HttpResponse<AuditLog[]>({ body: [], status: 200, statusText: 'OK' }))));
  }
}
